import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, Inbox, RefreshCw, Users, Building2, HeartPulse, CalendarCheck } from 'lucide-react';
import { fetchPersonalEmpleados } from './Services/personal.services';
import { PERSONAL_SECTIONS } from './PersonalLayout';
import { parseFechaIngreso, formatearFecha } from './utils/fechas';

const getNombreLavanderia = (empleado) => {
  const lav = empleado?.lavanderia;
  if (!lav) return null;
  if (typeof lav === 'string') return lav;
  return lav.nombre || null;
};

const nombreCompleto = (empleado) =>
  [empleado?.nombre, empleado?.apellido].filter(Boolean).join(' ') || 'Sin nombre';

const KpiCard = ({ icon: Icon, titulo, valor, descripcion, indice }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay: indice * 0.05 }}
  >
    <Card>
      <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
        <div className="p-2 rounded-md bg-primary/15">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <CardTitle className="text-sm font-medium text-gray-600">{titulo}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-gray-800">{valor}</div>
        <p className="text-xs text-gray-500 mt-1">{descripcion}</p>
      </CardContent>
    </Card>
  </motion.div>
);

const PersonalResumenPage = () => {
  const [empleados, setEmpleados] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, total: totalRegistros } = await fetchPersonalEmpleados();
      setEmpleados(data);
      setTotal(totalRegistros);
    } catch (e) {
      setError(e.message || 'No fue posible cargar la información del personal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const metricas = useMemo(() => {
    const conLavanderia = new Set();
    let conEps = 0;
    let conFechaLegible = 0;

    empleados.forEach((emp) => {
      const lav = getNombreLavanderia(emp);
      if (lav) conLavanderia.add(lav);
      if (emp?.eps && String(emp.eps).trim()) conEps += 1;
      if (parseFechaIngreso(emp?.fechaIngreso)) conFechaLegible += 1;
    });

    return {
      lavanderias: conLavanderia.size,
      conEps,
      conFechaLegible,
      sinFechaLegible: empleados.length - conFechaLegible,
    };
  }, [empleados]);

  // Secciones aún no habilitadas: se listan para que quede claro qué falta y
  // en qué parte del módulo entra cada una.
  const pendientes = PERSONAL_SECTIONS
    .flatMap((grupo) => grupo.items.map((item) => ({ ...item, grupo: grupo.title })))
    .filter((item) => !item.enabled);

  const recientes = [...empleados]
    .filter((emp) => parseFechaIngreso(emp?.fechaIngreso))
    .sort((a, b) => parseFechaIngreso(b.fechaIngreso) - parseFechaIngreso(a.fechaIngreso))
    .slice(0, 5);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          icon={Users}
          titulo="Empleados"
          valor={loading ? '—' : total}
          descripcion={loading ? 'Cargando...' : 'Registros en el maestro'}
          indice={0}
        />
        <KpiCard
          icon={Building2}
          titulo="Lavanderías"
          valor={loading ? '—' : metricas.lavanderias}
          descripcion="Centros con personal asignado"
          indice={1}
        />
        <KpiCard
          icon={HeartPulse}
          titulo="Con EPS"
          valor={loading ? '—' : metricas.conEps}
          descripcion="De los empleados cargados"
          indice={2}
        />
        <KpiCard
          icon={CalendarCheck}
          titulo="Fecha de ingreso legible"
          valor={loading ? '—' : `${metricas.conFechaLegible}/${empleados.length}`}
          descripcion={`${metricas.sinFechaLegible} sin formato reconocible`}
          indice={3}
        />
      </div>

      {error && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Card className="border-destructive/40">
            <CardContent className="flex flex-row items-center justify-between gap-4 p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
                <p className="text-sm text-gray-700">{error}</p>
              </div>
              <Button variant="outline" size="sm" onClick={cargar}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reintentar
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="text-base">Ingresos recientes</CardTitle>
              <CardDescription>Empleados ordenados por fecha de ingreso</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-gray-500">Cargando...</p>
              ) : recientes.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Inbox className="h-8 w-8 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">
                    Ningún empleado tiene una fecha de ingreso en un formato reconocible.
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Se normaliza al migrar el maestro de empleados.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {recientes.map((emp) => (
                    <li key={emp._id} className="py-2.5 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {nombreCompleto(emp)}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {emp.rol || 'Sin rol definido'}
                          {getNombreLavanderia(emp) ? ` · ${getNombreLavanderia(emp)}` : ''}
                        </p>
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap shrink-0">
                        {formatearFecha(parseFechaIngreso(emp.fechaIngreso))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.25 }}
        >
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="text-base">Próximamente</CardTitle>
              <CardDescription>Secciones del módulo aún no habilitadas</CardDescription>
            </CardHeader>
            <CardContent>
              {pendientes.length === 0 ? (
                <p className="text-sm text-gray-500">Todas las secciones están activas.</p>
              ) : (
                <ul className="space-y-2">
                  {pendientes.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li
                        key={item.to}
                        className="flex items-center gap-3 p-3 rounded-lg bg-gray-50"
                      >
                        <div className="p-1.5 rounded-md bg-gray-100">
                          <Icon className="h-4 w-4 text-gray-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-gray-600">{item.label}</p>
                          <p className="text-xs text-gray-400">{item.grupo}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PersonalResumenPage;